import CategoryMeasurementSection from '@c/CategoryMeasurementSection';
import FormCurrencyInputFancy from '@c/forms/controls/FormCurrencyInputFancy';
import FormLabel from '@c/forms/controls/FormLabel';
import DynamicMeasurementSection from '@c/listing/components/DynamicMeasurementSection';
import ShippingLabel from '@c/listing/components/ShippingLabel';
import PageSection from '@c/listing/shared/PageSection';
import ShippingLabelBanner from '@c/listing/ShippingLabelBanner';
import AddAddressModal from '@c/modals/AddAddressModal';
import { PlusCircleIcon } from '@heroicons/react/20/solid';
import RadioButton from '@ui/RadioButton';
import Toggle from '@ui/Toggle';
import AnimatedToggle from '@ui/AnimatedToggle';
import { getNumber, getShippingRegionOption } from '@util/createListingHelpers';
import { useUserAddresses } from '@util/hooks/useUserAddresses';
import { useAuth } from 'context/AuthContext';
import { Dimensions, ProductDocument, Shipment, Weight } from 'models/product';
import { useEffect, useState } from 'react';
import { useCreateListing } from '../_context';
import { CheckItem } from './MediaSelect';
import Select from '@ui/Select';
import { CategoryDocument } from 'models/category';

export const Shipping = () => {
  const { form, product, categoryInfo } = useCreateListing();
  const { userDoc } = useAuth();
  const allAddresses = useUserAddresses();
  const defaultShipping: ProductDocument['shipping_costs'] =
    userDoc?.username === 'road2recovery'
      ? [
          {
            code: 'US',
            cost: 15,
            name: 'United States',
            enabled: true,
          },
          {
            code: 'CA',
            cost: 15,
            name: 'Canada',
            enabled: true,
          },
          {
            code: 'MX',
            cost: 15,
            name: 'Mexico',
            enabled: true,
          },
          {
            code: 'AK',
            cost: 15,
            name: 'Alaska',
            enabled: true,
          },
          {
            code: 'HI',
            cost: 15,
            name: 'Hawaii',
            enabled: true,
          },
          {
            code: 'PR',
            cost: 15,
            name: 'Puerto Rico',
            enabled: true,
          },
          {
            code: 'EU',
            cost: 15,
            name: 'Europe',
            enabled: true,
          },
          {
            code: 'AU',
            cost: 15,
            name: 'Australia',
            enabled: true,
          },
          {
            code: 'SAM',
            cost: 15,
            name: 'South America',
            enabled: true,
          },
          {
            code: 'OT',
            cost: 15,
            name: 'Other',
            enabled: true,
          },
        ]
      : [
          {
            code: 'US',
            cost: 0,
            name: 'United States',
            enabled: true,
          },
        ];

  const defaultMxShippingLabel: Omit<Shipment, 'ship_from'> = {
    package: {
      weight: {
        value: 0,
        unit: 'ounce',
      },
      dimensions: {
        unit: 'inch',
        length: 0,
        width: 0,
        height: 0,
      },
      insurance_provider: 'none',
      insured_value: {
        currency: 'usd',
        amount: 0,
      },
    },
  };
  const [insuranceEnabled, setInsuranceEnabled] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);

  function handleAddLocationClick(opt: any) {
    const shippingRegions = product.shipping_costs ?? [];
    const newShippingRegions = shippingRegions.concat({
      code: opt.id,
      name: opt.label,
      cost: 0,
      enabled: true,
    });
    // set the enabled to true
    form.setValue('shipping_costs', newShippingRegions);
  }
  function handleLocationRemoveClick(opt: any) {
    const shippingRegions = product.shipping_costs ?? [];
    const newShippingRegions = shippingRegions.filter((r) => {
      return r.code !== opt.id;
    });
    form.setValue('shipping_costs', newShippingRegions);
  }

  function updateRegionalShippingCost(opt: any, cost: number | '') {
    const shippingRegions = product.shipping_costs ?? [];
    const newShippingRegions = shippingRegions.map((r) => {
      if (r.code === opt.id) {
        return {
          ...r,
          cost,
        };
      }
      return r;
    });
    form.setValue('shipping_costs', newShippingRegions as any);
  }
  const errorMessage = Array.isArray(form.formState.errors.shipping_costs)
    ? form.formState.errors.shipping_costs?.filter(
        (o: any) => o?.cost.message
      )[0]?.cost?.message
      ? 'All enabled shipping regions must have a cost'
      : ''
    : form.formState.errors.shipment?.package?.insured_value?.message ?? '';

  function toggleFlatRateShipping(enabled: boolean) {
    if (enabled) {
      form.setValue(
        'shipping_costs',
        product?.shipping_costs ?? product.shipping_costs ?? defaultShipping
      );
      form.setValue('shipment', undefined);
    } else {
      form.setValue(
        'shipment',
        product.shipment ??
          product?.shipment ?? {
            ...defaultMxShippingLabel,
            ...(userDoc?.addresses?.[0]! && {
              ship_from: userDoc.addresses[0],
            }),
          }
      );
    }
    form.setValue('is_flat_rate', enabled);
  }

  useEffect(() => {
    if (!product.is_flat_rate && !product.shipment?.package.dimensions?.unit) {
      form.setValue('shipment.package.dimensions.unit', 'inch');
    }
  }, [product.is_flat_rate, product.shipment?.package?.dimensions?.unit, form]);

  const handleShippingFromChange = (option: { value: string }) => {
    const address = allAddresses.find(
      (a) =>
        `${a.address_line1}, ${a.city_locality}, ${a.state_province} ${a.postal_code}` ===
        option.value
    );
    if (address) {
      form.setValue('shipment.ship_from', address);
    }
  };

  const catsWithWeight = categoryInfo.filter((c) => !!c.package?.weight);
  const selectedCat: CategoryDocument | undefined =
    catsWithWeight[catsWithWeight.length - 1];
  return (
    <PageSection
      title="Shipping"
      error={
        form.formState.errors.shipping_costs?.message ||
        form.formState.errors.shipment?.package?.message ||
        errorMessage
      }
    >
      <div className="flex w-full flex-col gap-x-16 sm:flex-row">
        <div className="w-full">
          <AddAddressModal
            isOpen={addressModalOpen}
            dismiss={(address) => {
              setAddressModalOpen(false);
              if (address && !product.is_flat_rate) {
                form.setValue('shipment.ship_from', address);
              }
            }}
          />

          <div className="mt-8 flex w-full gap-[3.2rem]">
            <div className="flex flex-col gap-[1.6rem]">
              {!product.is_auction && (
                <>
                  <FormLabel
                    value="Choose Shipping Fulfillment"
                    className="!text-[1.8rem]"
                    required
                  >
                    <div className="mt-8 hidden flex-wrap gap-[2.4rem] md:flex">
                      <div className="flex flex-col gap-4">
                        <RadioButton
                          label="Use MX Locker Shipping Label"
                          checked={
                            !product?.is_flat_rate && !product.is_auction
                          }
                          onClick={() => toggleFlatRateShipping(false)}
                        />
                        <span className="text-[1.3rem] text-brand-dark-gray">
                          Buyer Pays For Shipping at Checkout. You are provided
                          with a prepaid label.
                        </span>
                      </div>

                      <div className="flex flex-col gap-4">
                        <RadioButton
                          label="Use My Own Shipping Label"
                          checked={product?.is_flat_rate || product.is_auction}
                          onClick={() => toggleFlatRateShipping(true)}
                        />
                        <span className="text-[1.3rem] text-brand-dark-gray">
                          You are responsible for providing your own shipping
                          label once the item sells.
                        </span>
                      </div>
                    </div>
                  </FormLabel>

                  <div className="mt-8 flex w-full md:hidden">
                    <AnimatedToggle
                      onChange={(val) => toggleFlatRateShipping(val)}
                      value={product?.is_flat_rate ?? false}
                      options={[
                        { label: 'MX Locker Shipping', value: false },
                        { label: 'Ship on your own', value: true },
                      ]}
                      trackClassName="h-20 w-full rounded-full bg-[#d9d9d9]"
                      pillClassName="rounded-full bg-white"
                      buttonClassName="px-11 text-body-sm font-semibold"
                      activeTextClassName="text-[#222222]"
                      inactiveTextClassName="text-black hover:text-gray-900"
                    />
                  </div>
                </>
              )}

              <div className="my-8 hidden sm:block">
                <ShippingLabelBanner isFlatRate={product?.is_flat_rate} />
              </div>

              <div className="mt-8 sm:mt-16">
                {!product?.is_flat_rate && !product.is_auction && (
                  <>
                    <FormLabel
                      value="Shipping From"
                      className="!text-[1.8rem]"
                      required={true}
                    >
                      <div className="flex items-center gap-x-4 ">
                        <div className="w-full max-w-[30rem] sm:max-w-[unset]">
                          {!userDoc?.roles?.admin && (
                            <Select
                              allowEmpty={false}
                              error={
                                !!form.formState.errors?.shipment?.ship_from
                              }
                              options={allAddresses.map((address) => ({
                                id: `${address.address_line1}, ${address.city_locality}, ${address.state_province} ${address.postal_code}`,
                                value: `${address.address_line1}, ${address.city_locality}, ${address.state_province} ${address.postal_code}`,
                                label: `${address.address_line1}, ${address.city_locality}, ${address.state_province} ${address.postal_code}`,
                              }))}
                              value={
                                product.shipment?.ship_from
                                  ? `${product.shipment.ship_from.address_line1}, ${product.shipment.ship_from.city_locality}, ${product.shipment.ship_from.state_province} ${product.shipment.ship_from.postal_code}`
                                  : ''
                              }
                              onChange={handleShippingFromChange}
                            />
                          )}
                        </div>

                        {!!form.formState.errors?.shipment?.ship_from && (
                          <span className="text-[1.5rem] text-brand-secondary">
                            {
                              form.formState.errors?.shipment?.ship_from
                                ?.message
                            }
                          </span>
                        )}

                        <button
                          type="button"
                          className="flex h-[4rem] w-[4rem] items-center justify-center rounded-xl bg-brand-secondary p-2 hover:bg-brand-secondary-darker"
                          onClick={() => setAddressModalOpen(true)}
                        >
                          <PlusCircleIcon className="h-full w-full text-white" />
                        </button>
                      </div>
                    </FormLabel>

                    <ShippingLabel
                      form={product}
                      register={form.register}
                      setError={form.setError}
                      clearErrors={form.clearErrors}
                      handleWeightChange={(weight: number) => {
                        if (
                          selectedCat?.pkg_ranges?.weight?.min &&
                          weight < selectedCat.pkg_ranges?.weight?.min * 16
                        ) {
                          form.setError('shipment.package.weight', {
                            message: `Weight must be at least ${selectedCat.pkg_ranges?.weight?.min} lbs`,
                          });
                        } else if (
                          selectedCat?.pkg_ranges?.weight?.max &&
                          weight > selectedCat.pkg_ranges?.weight?.max * 16
                        ) {
                          form.setError('shipment.package.weight', {
                            message: `Weight must be at most ${selectedCat.pkg_ranges?.weight?.max} lbs`,
                          });
                        } else {
                          form.clearErrors('shipment.package.weight');
                        }
                        form.setValue('shipment.package.weight', {
                          value: weight,
                          unit: 'ounce',
                        });
                      }}
                      measurementSection={
                        product.category !== 'Collectibles' ? (
                          <DynamicMeasurementSection
                            selectedCat={selectedCat}
                            onSelect={(m: {
                              weight: Weight;
                              dimensions: Dimensions;
                            }) => {
                              const weight = m.weight;
                              const dimensions = m.dimensions;

                              form.setValue('shipment.package.dimensions', {
                                length: dimensions.length,
                                width: dimensions.width,
                                height: dimensions.height,
                                unit: 'inch',
                              });
                              form.setValue('shipment.package.weight', {
                                value: weight.value,
                                unit: 'ounce',
                              });
                            }}
                          />
                        ) : (
                          <CategoryMeasurementSection
                            category={'Collectibles'}
                            weight={{
                              value: 0,
                              unit: 'ounce',
                            }}
                            dimensions={{
                              length: 0,
                              width: 0,
                              height: 0,
                              unit: 'inch',
                            }}
                            onSelect={(m: {
                              weight: Weight;
                              dimensions: Dimensions;
                            }) => {
                              const weight = m.weight;
                              const dimensions = m.dimensions;
                              form.setValue('shipment.package.dimensions', {
                                length: dimensions.length,
                                width: dimensions.width,
                                height: dimensions.height,
                                unit: 'inch',
                              });
                              form.setValue('shipment.package.weight', {
                                value: weight.value,
                                unit: 'ounce',
                              });
                            }}
                          />
                        )
                      }
                      insuranceEnabled={insuranceEnabled}
                      toggleShipInsurance={() => {
                        form.clearErrors('shipment.package.insured_value');

                        if (product.shipment?.package.insured_value?.amount) {
                          form.setValue('shipment.package.insured_value', {
                            currency: 'usd',
                            amount: 0,
                          });

                          form.setValue(
                            'shipment.package.insurance_provider',
                            'none'
                          );
                        } else {
                          form.setValue('shipment.package.insured_value', {
                            currency: 'usd',
                            amount: product.price ? Number(product.price) : 0,
                          });

                          form.setValue(
                            'shipment.package.insurance_provider',
                            'mx_locker'
                          );
                        }

                        setInsuranceEnabled(!insuranceEnabled);
                      }}
                      errors={form.formState.errors}
                      hideInsuranceOption={false}
                      selectedCat={selectedCat}
                    />
                  </>
                )}
              </div>

              {(product?.is_flat_rate || product.is_auction) && (
                <FormLabel
                  value="Shipping To"
                  className="!text-[1.8rem]"
                  required={true}
                >
                  <div className="flex flex-col gap-[1.6rem]">
                    <p>$0 equals Free Shipping</p>

                    <div className="mt-8 grid w-full grid-cols-1 justify-between gap-y-[1.6rem] lg:grid-cols-2">
                      {getShippingRegionOption().map((opt) => (
                        <FormLabel value={opt.label} key={opt.id}>
                          <div className="flex items-center gap-[1.6rem]">
                            <div className="max-w-[20rem]">
                              <FormCurrencyInputFancy
                                placeholder={'Enter a cost'}
                                allowEmpty
                                value={
                                  (product?.shipping_costs &&
                                    product?.shipping_costs
                                      ?.find((o) => o.code === opt.id)
                                      ?.cost?.toString()) ??
                                  ''
                                }
                                onChange={(e) => {
                                  updateRegionalShippingCost(
                                    opt,
                                    e.target.value !== ''
                                      ? getNumber(e.target.value)
                                      : ''
                                  );
                                }}
                                defaultValue={
                                  (product?.shipping_costs &&
                                    product?.shipping_costs
                                      ?.find((o) => o.code === opt.id)
                                      ?.cost?.toString()) ??
                                  ''
                                }
                              />
                            </div>

                            <Toggle
                              enabled={
                                !!(
                                  product?.shipping_costs &&
                                  product?.shipping_costs?.findIndex(
                                    (o) => o.code === opt.id
                                  ) > -1
                                )
                              }
                              onChange={() => {
                                const isEnabled =
                                  product?.shipping_costs &&
                                  product?.shipping_costs?.findIndex(
                                    (o) => o.code === opt.id
                                  ) > -1;
                                if (isEnabled) {
                                  handleLocationRemoveClick(opt);
                                } else {
                                  handleAddLocationClick(opt);
                                }
                              }}
                            />
                          </div>
                        </FormLabel>
                      ))}

                      <div className="mt-8">
                        <p className="text-[1.5rem] text-zinc-600 sm:w-[85rem]">
                          By selecting manual shipping, you are assuming
                          responsibility for any issues that arise in shipping
                          and handling. Please consider purchasing shipping
                          insurance for big-ticket items!
                        </p>
                      </div>
                    </div>
                  </div>
                </FormLabel>
              )}
            </div>
          </div>
        </div>

        <div className="hidden flex-col gap-y-6 sm:mt-0 sm:flex">
          <h3 className="text-[1.8rem] font-semibold text-black">
            We recommend using MX Shipping Label
          </h3>

          <CheckItem text="We strongly encourage sellers to use our suggested weight and dimensions" />
          <CheckItem text="Use MX shipping label will make it easier for shipping and automatic tracking" />
          <CheckItem text="Offer a free shipping to attract more buyers" />
        </div>
      </div>
    </PageSection>
  );
};
